// ═══════════════════════════════════════════════════════════════
// CHUNK LOAD RECOVERY — Global stale-chunk / SPA navigation guard
//
// Problema que resuelve:
//   El Admin es una SPA con code-splitting (React.lazy + import()).
//   Tras un deploy nuevo, una pestaña ya abierta conserva el grafo de
//   módulos del build anterior. Al navegar por el sidebar, se intenta
//   cargar un chunk `assets/page-[hash].js` que ya no existe en el
//   servidor → 404 → la promesa de import() rechaza → sin boundary la
//   app queda en blanco.
//
// Estrategia:
//   1. Detectar SOLO errores reales de carga de módulo/chunk.
//   2. Permitir UNA SOLA recuperación automática por incidente/build.
//   3. La recuperación recarga la URL/ruta actual para obtener el build
//      vigente (una sola vez).
//   4. Un flag en sessionStorage impide loops infinitos
//      (reload → error → reload → error ...).
//   5. El flag se limpia solo cuando la app cargó ESTABLE (sin errores
//      de chunk en esta carga), para que un incidente futuro (otro
//      deploy) vuelva a tener derecho a su recuperación.
//
// NO cambia la navegación normal: el sidebar sigue siendo SPA pura.
// Solo actúa cuando un chunk realmente falla.
// ═══════════════════════════════════════════════════════════════

const RECOVERY_FLAG = 'ip_chunk_recovery_attempted';
const RECOVERY_TS_FLAG = 'ip_chunk_recovery_ts';

// Marca en memoria: ¿ocurrió un error de chunk durante ESTA carga?
// Si es true, NO limpiamos el flag (evita loop de reloads).
let _chunkErrorThisLoad = false;

/**
 * ¿El error corresponde a un fallo real de carga de módulo/chunk?
 * Cubre los mensajes reales de los navegadores/build actuales.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;

  let name = '';
  let message = '';
  try {
    const err = error as { name?: unknown; message?: unknown };
    name = String(err?.name ?? '');
    message = String(err?.message ?? '');
    if (!name && !message) {
      message = String(error);
    }
  } catch {
    message = String(error);
  }

  const haystack = `${name} ${message}`.toLowerCase();

  return (
    haystack.includes('failed to fetch dynamically imported module') ||
    haystack.includes('importing a module script failed') ||
    haystack.includes('error loading dynamically imported module') ||
    haystack.includes('failed to load module script') ||
    haystack.includes('dynamically imported module') ||
    haystack.includes('chunkloaderror') ||
    haystack.includes('loading chunk') ||
    haystack.includes('loading css chunk') ||
    haystack.includes('unable to preload css') ||
    (haystack.includes('preload') && haystack.includes('module'))
  );
}

/** ¿Ya se usó la única recuperación automática permitida en este incidente? */
export function hasAttemptedChunkRecovery(): boolean {
  try {
    return sessionStorage.getItem(RECOVERY_FLAG) === '1';
  } catch {
    return false;
  }
}

/** Registrar que ya se intentó la recuperación automática. */
export function markChunkRecoveryAttempted(): void {
  try {
    sessionStorage.setItem(RECOVERY_FLAG, '1');
    sessionStorage.setItem(RECOVERY_TS_FLAG, String(Date.now()));
  } catch {
    /* ignore storage errors */
  }
}

/** Limpiar el flag (solo cuando la carga fue estable). */
export function clearChunkRecoveryFlag(): void {
  try {
    sessionStorage.removeItem(RECOVERY_FLAG);
    sessionStorage.removeItem(RECOVERY_TS_FLAG);
  } catch {
    /* ignore storage errors */
  }
}

/**
 * Intenta UNA SOLA recuperación automática.
 * @returns true si esta llamada disparó la recarga; false si ya se había
 *          intentado (evita loops infinitos).
 */
export function recoverFromChunkError(): boolean {
  _chunkErrorThisLoad = true;

  if (hasAttemptedChunkRecovery()) {
    // Ya se intentó antes en este incidente → NO volver a recargar.
    return false;
  }

  markChunkRecoveryAttempted();
  console.warn(
    '[ChunkRecovery] Stale chunk detected — performing ONE automatic reload to fetch the current build.',
  );

  try {
    window.location.reload();
  } catch {
    /* ignore reload errors */
  }
  return true;
}

/**
 * Llamar cuando la app ya cargó y se mantuvo estable (sin errores de chunk).
 * Solo entonces liberamos el flag para permitir una recuperación futura.
 */
export function notifyAppLoadedStable(): void {
  if (_chunkErrorThisLoad) return; // hubo error → conservar el flag (anti-loop)
  clearChunkRecoveryFlag();
}

/**
 * Instala los listeners globales (idempotente).
 * - `vite:preloadError`: lo dispara Vite cuando un import()/preload de un
 *   chunk con hash falla (caso típico de chunk obsoleto tras un deploy).
 * - `unhandledrejection`: cubre rechazos de import() que no pasan por render.
 *
 * IMPORTANTE: no interfiere con el listener de `unhandledrejection` de
 * supabase.ts (ese solo actúa sobre errores de refresh token).
 */
export function installChunkLoadRecovery(): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as { __ipChunkRecoveryInstalled?: boolean };
  if (w.__ipChunkRecoveryInstalled) return;
  w.__ipChunkRecoveryInstalled = true;

  window.addEventListener('vite:preloadError', (event) => {
    // Cualquier preloadError de Vite es, por definición, un chunk que no se
    // pudo precargar (típicamente un hash obsoleto). Intentar una recuperación.
    const evt = event as Event & { payload?: unknown };
    if (evt.payload && !isChunkLoadError(evt.payload)) {
      // Algunos builds adjuntan el error real; si no coincide, inferimos igual
      // que es un chunk obsoleto porque este evento solo se dispara por eso.
    }
    event.preventDefault?.();
    recoverFromChunkError();
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (!isChunkLoadError(event?.reason)) return;
    const didReload = recoverFromChunkError();
    if (didReload) event.preventDefault();
  });
}